import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { COUNTRIES, formatUSD } from '../data/countries';

const REGION_ORDER = [
  { key: 'europe', label: 'Europe' },
  { key: 'asia', label: 'Asia' },
  { key: 'americas', label: 'Americas' },
  { key: 'middle_east', label: 'Middle East' },
  { key: 'africa', label: 'Africa' },
];

export default function CountriesMenu({ trigger = 'Countries' }){
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    // Autofocus search on open
    setTimeout(() => inputRef.current?.focus(), 30);
    // Lock body scroll on mobile when open
    const isMobile = window.matchMedia('(max-width: 1080px)').matches;
    if (isMobile) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [open]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? COUNTRIES.filter(c =>
          c.name.toLowerCase().includes(q) ||
          c.visaName.toLowerCase().includes(q) ||
          (c.capital || '').toLowerCase().includes(q) ||
          (c.regionLabel || '').toLowerCase().includes(q)
        )
      : COUNTRIES;
    const byRegion = {};
    REGION_ORDER.forEach(r => { byRegion[r.key] = []; });
    filtered.forEach(c => {
      if (byRegion[c.region]) byRegion[c.region].push(c);
    });
    // Alphabetise within region
    Object.keys(byRegion).forEach(k => byRegion[k].sort((a,b) => a.name.localeCompare(b.name)));
    return byRegion;
  }, [query]);

  const totalResults = Object.values(grouped).reduce((s, a) => s + a.length, 0);

  return (
    <div className={`cmenu-wrap ${open ? 'open' : ''}`} ref={wrapRef}>
      <button
        className="cmenu-trigger"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="countries-menu-trigger"
      >
        {trigger}
        <span className="cmenu-caret" aria-hidden="true"></span>
      </button>

      {open && (
        <>
          <div className="cmenu-backdrop" onClick={() => setOpen(false)} />
          <div className="cmenu-panel" role="dialog" aria-label="Select a country">
            <div className="cmenu-head">
              <div className="cmenu-title">Choose a country</div>
              <button className="cmenu-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="cmenu-search">
              <span className="cmenu-search-icon" aria-hidden="true">⌕</span>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by country, visa name, or capital…"
                data-testid="countries-menu-search"
              />
              {query && (
                <button className="cmenu-clear" onClick={() => setQuery('')} aria-label="Clear">×</button>
              )}
            </div>
            <div className="cmenu-results-count">
              {totalResults === 0
                ? 'No countries match — try a different search.'
                : `${totalResults} countr${totalResults === 1 ? 'y' : 'ies'}`}
            </div>
            <div className="cmenu-body">
              {REGION_ORDER.map(r => (
                grouped[r.key].length > 0 && (
                  <div key={r.key} className="cmenu-col">
                    <div className="cmenu-region">{r.label}</div>
                    <ul className="cmenu-list">
                      {grouped[r.key].map(c => (
                        <li key={c.slug}>
                          <Link
                            to={`/countries/${c.slug}`}
                            className="cmenu-item"
                            onClick={() => setOpen(false)}
                            data-testid={`cmenu-item-${c.slug}`}
                          >
                            <span className="cmenu-flag" aria-hidden="true">{c.flag}</span>
                            <span className="cmenu-name">
                              <strong>{c.name}</strong>
                              <small>{formatUSD(c.minIncome)}/mo · {c.validity}</small>
                            </span>
                            <span className="cmenu-arrow" aria-hidden="true">→</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
            </div>
            <div className="cmenu-foot">
              <Link to="/countries" className="cmenu-viewall" onClick={() => setOpen(false)}>View all countries →</Link>
              <Link to="/quiz" className="cmenu-quiz" onClick={() => setOpen(false)}>Not sure? Take the quiz</Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
